import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "./api";
import { logger } from "./logger";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Skip /me if returning from Emergent Google Auth callback
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const loginWithEmail = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("rivendas_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("rivendas_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Non-blocking: proceed to local cleanup even if server call fails
      logger.warn("logout server call failed:", err?.message);
    }
    localStorage.removeItem("rivendas_token");
    setUser(null);
  };

  const updateRole = async (role) => {
    const { data } = await api.patch("/auth/role", { role });
    setUser(data);
    return data;
  };

  const refresh = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      // Silent refresh; log for debugging only
      logger.warn("auth refresh failed:", err?.message);
    }
  };

  const value = useMemo(
    () => ({ user, loading, loginWithEmail, register, logout, updateRole, refresh, setUser }),
    [user, loading]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
